import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';

const C = {
  ink: '#0B0E17', surface: '#1E2235', border: '#2A2E45',
  muted: '#8892A4', gold: '#F0B429', teal: '#00C9B1',
  coral: '#FF6B6B', text: '#E8EDF5', sub: '#C5CDD8',
};

const PASSWORD_RULES = [
  { key: 'length', text: 'At least 8 characters', test: (password) => password.length >= 8 },
  { key: 'upper', text: 'At least 1 uppercase letter', test: (password) => /[A-Z]/.test(password) },
  { key: 'lower', text: 'At least 1 lowercase letter', test: (password) => /[a-z]/.test(password) },
  { key: 'special', text: 'At least 1 special character', test: (password) => /[^A-Za-z\d]/.test(password) },
  { key: 'number', text: 'At least 1 number', test: (password) => /\d/.test(password) },
];

function buildPasswordChecks(password) {
  return PASSWORD_RULES.map((rule) => ({ ...rule, met: rule.test(password) }));
}

function accessRequiresBilling(user) {
  if (!user) return false;

  if (user.plan === 'trial') {
    return user.subscriptionStatus !== 'trialing'
      || !user.subscriptionEndDate
      || new Date(user.subscriptionEndDate) <= new Date();
  }

  if (['basic', 'growth', 'enterprise', 'pro', 'business'].includes(user.plan)) {
    return Boolean(
      user.subscriptionStatus
      && !['active', 'cancelled'].includes(user.subscriptionStatus)
      && (!user.subscriptionEndDate || new Date(user.subscriptionEndDate) <= new Date())
    );
  }

  return false;
}

function EyeIcon({ visible }) {
  if (visible) {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20C7 20 2.73 16.89 1 12c.73-2.06 2-3.94 3.62-5.46" />
        <path d="M10.58 10.58a2 2 0 1 0 2.83 2.83" />
        <path d="M9.88 4.24A10.94 10.94 0 0 1 12 4c5 0 9.27 3.11 11 8a11.64 11.64 0 0 1-4.31 5.31" />
        <path d="M1 1l22 22" />
      </svg>
    );
  }

  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

const inputStyle = {
  width: '100%',
  padding: '10px 13px',
  background: 'rgba(255,255,255,0.04)',
  border: `1.5px solid ${C.border}`,
  borderRadius: 10,
  fontSize: '0.875rem',
  color: C.text,
  fontFamily: 'inherit',
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 150ms',
};

const labelStyle = {
  display: 'block',
  fontSize: '0.72rem',
  fontWeight: 700,
  color: C.muted,
  marginBottom: 6,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
};

function FormGroup({ label, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );
}

export default function Login({ defaultTab = 'login' }) {
  const [tab, setTab] = useState(defaultTab);
  const [form, setForm] = useState({
    name: '',
    businessName: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showResend, setShowResend] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const [resendSent, setResendSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const passwordChecks = buildPasswordChecks(form.password);
  const passwordValid = passwordChecks.every((rule) => rule.met);
  const passwordsMatch = form.password === form.confirmPassword;
  const registerValid = passwordValid && passwordsMatch;

  const set = (field) => (e) => setForm((current) => ({ ...current, [field]: e.target.value }));

  useEffect(() => {
    setTab(defaultTab);
  }, [defaultTab]);

  const switchTab = (nextTab) => {
    setTab(nextTab);
    setError('');
    setMessage('');
    setShowResend(false);
    setResendSent(false);
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setShowResend(false);
    setLoading(true);

    try {
      if (tab === 'login') {
        const { data } = await api.post('/auth/login', {
          email: form.email,
          password: form.password,
        });
        login(data.token, data.refreshToken, data.user);
        if (accessRequiresBilling(data.user)) {
          navigate('/pricing');
        } else {
          const done = localStorage.getItem('optika_onboarding_complete');
          navigate(done ? '/dashboard' : '/onboarding');
        }
      } else if (tab === 'forgot') {
        await api.post('/auth/forgot-password', { email: form.email });
        setMessage('If an account exists for that email, we sent a password reset link.');
      } else {
        if (!registerValid) {
          setError('Please meet the password requirements and confirm your password.');
          return;
        }

        await api.post('/auth/register', {
          name: form.name,
          businessName: form.businessName,
          phone: form.phone,
          email: form.email,
          password: form.password,
        });
        setTab('check-email');
        setUnverifiedEmail(form.email);
      }
    } catch (err) {
      const data = err.response?.data;
      if (['EMAIL_NOT_VERIFIED', 'EMAIL_VERIFICATION_EXPIRED', 'EMAIL_PENDING_VERIFICATION'].includes(data?.code)) {
        setError(data?.error || 'Please verify your email before logging in. Check your inbox.');
        setShowResend(true);
        setUnverifiedEmail(data.email || form.email);
      } else {
        setError(data?.error || 'Something went wrong');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      await api.post('/auth/resend-verification', { email: unverifiedEmail });
      setError('Verification email sent! Check your inbox.');
      setShowResend(false);
      setResendSent(true);
    } catch {
      setError('Failed to resend. Please try again.');
    }
  };

  const page = {
    minHeight: '100vh',
    background: C.ink,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Cabinet Grotesk', sans-serif",
    padding: 24,
  };

  const card = {
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 20,
    padding: 40,
    width: '100%',
    maxWidth: 420,
    boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
  };

  if (tab === 'check-email') {
    return (
      <div style={page}>
        <div style={{ ...card, textAlign: 'center' }}>
          <div
            style={{
              width: 64,
              height: 64,
              background: 'rgba(240,180,41,0.12)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              fontSize: 28,
            }}
          >
            ✉️
          </div>
          <h2 style={{ color: C.text, marginBottom: 8, fontSize: '1.3rem', fontWeight: 800 }}>Check your email</h2>
          <p style={{ color: C.muted, marginBottom: 8 }}>We sent a verification link to</p>
          <p style={{ fontWeight: 600, color: C.text, marginBottom: 24 }}>{unverifiedEmail}</p>
          <p style={{ color: C.muted, fontSize: '0.85rem', marginBottom: 24 }}>
            Click the link in the email to activate your account. The link expires in 24 hours.
          </p>
          {!resendSent ? (
            <button
              onClick={handleResend}
              style={{
                background: 'transparent',
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                padding: '10px 24px',
                fontSize: 14,
                cursor: 'pointer',
                color: C.gold,
                fontFamily: 'inherit',
              }}
            >
              Resend verification email
            </button>
          ) : (
            <p style={{ color: C.teal, fontSize: '0.85rem' }}>Email sent!</p>
          )}
          <p style={{ marginTop: 16, fontSize: '0.85rem', color: C.muted }}>
            Already verified?{' '}
            <button
              onClick={() => switchTab('login')}
              style={{
                background: 'none',
                border: 'none',
                color: C.gold,
                fontWeight: 700,
                cursor: 'pointer',
                padding: 0,
                fontFamily: 'inherit',
              }}
            >
              Sign in
            </button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={page}>
      <div style={card}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <Link to="/">
            <img
              src="/optika-logo.svg"
              alt="OptiKa"
              style={{ height: 40, width: 'auto', marginBottom: 8 }}
            />
          </Link>
          <p style={{ color: C.muted, fontSize: 13, margin: 0 }}>
            Business Intelligence for African SMEs
          </p>
        </div>

        <div style={{ display: 'flex', marginBottom: 24, background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: 4 }}>
          {['login', 'register'].map((currentTab) => (
            <button
              key={currentTab}
              onClick={() => switchTab(currentTab)}
              style={{
                flex: 1,
                padding: '8px',
                border: 'none',
                borderRadius: 7,
                cursor: 'pointer',
                background: tab === currentTab ? C.gold : 'transparent',
                fontWeight: tab === currentTab ? 700 : 500,
                color: tab === currentTab ? C.ink : C.muted,
                transition: 'all 150ms',
                fontFamily: 'inherit',
                fontSize: '0.875rem',
              }}
            >
              {currentTab === 'login' ? 'Sign In' : 'Register'}
            </button>
          ))}
        </div>

        {error && (
          <div style={{
            padding: '12px 14px',
            background: 'rgba(255,107,107,0.08)',
            border: '1px solid rgba(255,107,107,0.25)',
            borderRadius: 10,
            color: C.coral,
            fontSize: '0.85rem',
            marginBottom: 16,
          }}>
            {error}
            {showResend && (
              <button
                onClick={handleResend}
                style={{
                  display: 'block',
                  marginTop: 8,
                  background: 'none',
                  border: 'none',
                  color: C.coral,
                  fontWeight: 700,
                  cursor: 'pointer',
                  padding: 0,
                  fontSize: '0.85rem',
                  fontFamily: 'inherit',
                  textDecoration: 'underline',
                }}
              >
                Resend verification email →
              </button>
            )}
          </div>
        )}

        {message && !error && (
          <div
            style={{
              marginBottom: 16,
              padding: '12px 14px',
              borderRadius: 10,
              background: 'rgba(0,201,177,0.08)',
              border: '1px solid rgba(0,201,177,0.25)',
              color: C.teal,
              fontSize: '0.9rem',
            }}
          >
            {message}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {tab === 'register' && (
            <>
              <FormGroup label="Full Name">
                <input style={inputStyle} placeholder="Jane Doe" value={form.name} onChange={set('name')} required />
              </FormGroup>
              <FormGroup label="Business Name">
                <input
                  style={inputStyle}
                  placeholder="e.g. Sunrise General Store"
                  value={form.businessName}
                  onChange={set('businessName')}
                  required
                />
              </FormGroup>
              <FormGroup label="Telephone Number">
                <input
                  style={inputStyle}
                  type="tel"
                  placeholder="+233 24 123 4567"
                  value={form.phone}
                  onChange={set('phone')}
                  required
                />
              </FormGroup>
            </>
          )}

          <FormGroup label="Email">
            <input style={inputStyle} type="email" placeholder="you@example.com" value={form.email} onChange={set('email')} required />
          </FormGroup>

          {tab !== 'forgot' && (
            <FormGroup label="Password">
              <div style={{ position: 'relative' }}>
                <input
                  style={{ ...inputStyle, paddingRight: 44 }}
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={form.password}
                  onChange={set('password')}
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((current) => !current)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    right: 12,
                    transform: 'translateY(-50%)',
                    border: 'none',
                    background: 'transparent',
                    color: C.muted,
                    cursor: 'pointer',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <EyeIcon visible={showPassword} />
                </button>
              </div>
              {tab === 'register' && (
                <div
                  style={{
                    marginTop: 8,
                    padding: '10px 14px',
                    background: 'rgba(255,255,255,0.03)',
                    borderRadius: 8,
                    border: `1px solid ${C.border}`,
                  }}
                >
                  <p style={{ fontSize: 11, color: C.muted, fontWeight: 600, marginBottom: 6 }}>
                    Password requirements: At least 8 characters, 1 uppercase, 1 lowercase, 1 special character, and 1 number
                  </p>
                  {passwordChecks.map((rule) => (
                    <div key={rule.key} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                      <span style={{ fontSize: 12, color: rule.met ? C.teal : C.muted }}>{rule.met ? '✓' : '○'}</span>
                      <span style={{ fontSize: 12, color: rule.met ? C.teal : C.sub }}>{rule.text}</span>
                    </div>
                  ))}
                </div>
              )}
            </FormGroup>
          )}

          {tab === 'register' && (
            <FormGroup label="Confirm Password">
              <div style={{ position: 'relative' }}>
                <input
                  style={{ ...inputStyle, paddingRight: 44 }}
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Re-enter your password"
                  value={form.confirmPassword}
                  onChange={set('confirmPassword')}
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((current) => !current)}
                  aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    right: 12,
                    transform: 'translateY(-50%)',
                    border: 'none',
                    background: 'transparent',
                    color: C.muted,
                    cursor: 'pointer',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <EyeIcon visible={showConfirmPassword} />
                </button>
              </div>
              {form.confirmPassword.length > 0 && !passwordsMatch && (
                <p style={{ marginTop: 8, fontSize: '0.8rem', color: C.coral }}>Passwords do not match.</p>
              )}
            </FormGroup>
          )}

          <button
            type="submit"
            disabled={loading || (tab === 'register' && !registerValid)}
            style={{
              width: '100%',
              justifyContent: 'center',
              padding: 12,
              marginTop: 8,
              background: C.gold,
              color: C.ink,
              border: 'none',
              borderRadius: 10,
              fontWeight: 700,
              fontSize: '0.9rem',
              fontFamily: 'inherit',
              opacity: loading || tab !== 'register' || registerValid ? 1 : 0.5,
              cursor: loading || (tab === 'register' && !registerValid) ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? 'Please wait...' : tab === 'login' ? 'Sign In' : tab === 'forgot' ? 'Send Reset Link' : 'Create Account'}
          </button>
        </form>

        {tab === 'login' && (
          <>
            <p style={{ textAlign: 'center', marginTop: 16, fontSize: '0.85rem' }}>
              <button
                onClick={() => switchTab('forgot')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: C.gold,
                  fontWeight: 700,
                  cursor: 'pointer',
                  padding: 0,
                  fontFamily: 'inherit',
                }}
              >
                Forgot password?
              </button>
            </p>
            <p style={{ textAlign: 'center', marginTop: 8, fontSize: '0.85rem', color: C.muted }}>
              No account?{' '}
              <Link to="/register" style={{ color: C.gold, fontWeight: 700, textDecoration: 'none' }}>
                Start your 14-day trial
              </Link>
            </p>
          </>
        )}

        {tab === 'forgot' && (
          <p style={{ textAlign: 'center', marginTop: 16, fontSize: '0.85rem', color: C.muted }}>
            Remembered your password?{' '}
            <button
              onClick={() => switchTab('login')}
              style={{
                background: 'none',
                border: 'none',
                color: C.gold,
                fontWeight: 700,
                cursor: 'pointer',
                padding: 0,
                fontFamily: 'inherit',
              }}
            >
              Sign in
            </button>
          </p>
        )}
      </div>
    </div>
  );
}
